'use client'
import { Case, Media } from '@/payload-types'
import RenderRichTexComponent from '../rich-text-render/richTextRender'
import InfoCard from '../info-card/infoCard'
import Card, { CardTitle } from '../card/card'
import EmbeddedGalleryComponent from '../galleries/embedded-gallery/embeddedGallery'
import CaseProfileCard from '../case-profile-card/caseProfileCard'
import EventsTimeLine from '../events-time-line/eventsTimeLine'
import { capitalizeFirstLetter, formatDate } from '@/lib/caseFunctions'
import { MediaCarousel } from '../galleries/media-carousel/mediaCarousel'
import { useMemo } from 'react'

interface IProps {
  data?: Case
  featuredIncidents?: Case[]
}

const RenderCasePageComponent = ({ data, featuredIncidents }: IProps) => {
  const dateCard = () => (
    <InfoCard
      icon="CalendarMonth"
      title="Date"
      items={[{ label: formatDate(data?.date ?? '') }]}
      className="md:min-w-[100px] lg:min-w-[150px] md:w-[180px]"
    />
  )
  const locationCard = () => (
    <InfoCard
      icon="MapPin"
      title="Location"
      items={[{ label: data?.address ?? '' }]}
      className="md:min-w-[200px] lg:min-w-[330px] md:max-w-[1160px]"
    />
  )
  const universityCard = () => {
    const universities = data?.universities?.map(u => (
      { 
        label: typeof u === 'object' ? u?.name ?? '' : ''
      }
      )) ?? []
    return (
    <InfoCard
      icon="Building"
      title="University"
      items={universities}
      className="md:min-w-[200px] md:max-w-[1160px]"
    />
  )}

  const organizationsCard = () => {
    const organizations = data?.organizations?.map(o => (
      { 
        label: typeof o === 'object' ? o?.name ?? '' : '', 
        link: (typeof o === 'object' && typeof o?.publicOrgProfile === 'object' && o?.publicOrgProfile?._status === 'published') ? `/organization/${o?.publicOrgProfile?.slug || ''}` : '' 
      }
      )) ?? []
    return (<InfoCard
      icon="UsersGroup"
      title="Organizations"
      items={organizations}
      className="md:min-w-[160px] md:max-w-[400px]"
      previewCount={6}
    />
  )}

  const publishedProfiles = useMemo(
    () =>
      data?.profiles?.filter(
        (p) =>
          typeof p === 'object' &&
          typeof p.publicProfile === 'object' &&
          p.publicProfile?._status === 'published',
      ) ?? [],
    [data?.profiles],
  )
  
  // this code is to put the cover image at the top of the gallery medias, and remove the duplicate cover image
  const galleryMedias: (string | Media)[] = useMemo(() => {
    const topMedias = data?.topMedias?.map(m => typeof m === 'object' ? m : null).filter(m => m !== null) || []
    const coverimage = typeof data?.coverimage === 'object' ? data?.coverimage : null
    const coverImageInTopMedias = topMedias?.some(m => m?.id === coverimage?.id)
    return [...(coverimage ? [coverimage] : []), ...(coverImageInTopMedias ? topMedias?.filter(m => m?.id !== coverimage?.id) : topMedias)]
  }, [data?.coverimage, data?.topMedias])
  
  return (
    <div className="PageInternalContainer">
      {/* Title */}
      <h1 className="text-4xl font-bold border-b border-black pb-2">
        {data?.title}
      </h1>

      {/* Description */}
      <div className="py-2">
        <RenderRichTexComponent richTextField={data?.shortDescription} inline />
      </div>

      <div className="flex flex-col gap-4">
        {/* Info cards for desktop */}
        <div className="hidden md:flex gap-4">
          {dateCard()}
          {locationCard()}
          {universityCard()}
          {organizationsCard()}
        </div>

        {/* Info cards for mobile */}
        <div className="flex md:hidden w-full gap-2">
          <div className="flex flex-col gap-2 basis-0 grow min-w-0">
            {dateCard()}
            {universityCard()}
          </div>
          <div className="flex flex-col gap-2 basis-0 grow min-w-0">
            {locationCard()}
            {organizationsCard()}
          </div>
        </div>

        {/* Media + Profiles */}
        <div className="flex flex-col md:flex-row items-start gap-4 md:h-[490px]">
          {galleryMedias?.length ? (
            <Card className="md:w-[55%] w-full h-full flex flex-col">
              <CardTitle>
                {`${capitalizeFirstLetter(data?.type ?? '')} Media`}
              </CardTitle>
              <div className="h-fit">
                {galleryMedias.length > 2 ? (
                  <EmbeddedGalleryComponent
                    mediaBlocks={galleryMedias.map((m) => ({
                      media: m,
                      blockType: 'mediaBlock',
                    }))}
                  />
                ) : (
                  <div className="w-auto md:h-[420px] h-[270px]">
                    <MediaCarousel media={galleryMedias} />
                  </div>
                )}
              </div>
            </Card>
          ) : null}

          {publishedProfiles.length > 0 && (
            <Card className="md:w-[45%] w-full md:h-full h-[520px] flex flex-col">
              <CardTitle>
                {`${capitalizeFirstLetter(data?.type ?? '')} Profiles`}
              </CardTitle>
              <div className="flex flex-col flex-grow gap-3 w-full overflow-y-auto">
                {publishedProfiles.map((p) =>
                  typeof p === 'object' ? (
                    <CaseProfileCard
                      key={p.id}
                      profileData={p}
                      caseId={data?.id}
                    />
                  ) : null,
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Featured incidents */}
        {featuredIncidents?.length ? (
          <Card>
            <CardTitle>Featured Incidents</CardTitle>
            <EventsTimeLine
              events={featuredIncidents
                .filter((i) => i.type === 'incident')
                .sort(
                  (a, b) =>
                    new Date(b.date ?? '').getTime() -
                    new Date(a.date ?? '').getTime(),
                )}
            />
          </Card>
        ) : null}
      </div>
    </div>
  )
}

export default RenderCasePageComponent
